import { useState, useEffect, useRef } from 'react';
import api from '../services/api';
import { API } from '../App';
import { toast } from 'sonner';
import {
  Users, Globe, Link2, BarChart2, FileText, Folder, Star,
  Settings, Mail, Calendar, Video, Shield, Plus, Trash2,
  ChevronDown, ChevronRight, Upload, Check, X, Loader2,
  FileSpreadsheet, Gavel, Wrench, ExternalLink, AlertCircle,
} from 'lucide-react';

const enconOrange = '#ee7203';

// ── Tab definitions ───────────────────────────────────────────────────────────
const TABS = [
  { id: 'interview_notes', label: 'Interview Notes',       Icon: FileText },
  { id: 'disciplinary',    label: 'Disciplinary Questions', Icon: Gavel },
  { id: 'sdp_crm',        label: 'SDP / CRM',             Icon: FileSpreadsheet },
];

// ── Pre-loaded icon set ───────────────────────────────────────────────────────
const PRELOADED_ICONS = [
  { name: 'globe',      Icon: Globe },
  { name: 'link',       Icon: Link2 },
  { name: 'chart',      Icon: BarChart2 },
  { name: 'file',       Icon: FileText },
  { name: 'folder',     Icon: Folder },
  { name: 'star',       Icon: Star },
  { name: 'settings',   Icon: Settings },
  { name: 'mail',       Icon: Mail },
  { name: 'calendar',   Icon: Calendar },
  { name: 'video',      Icon: Video },
  { name: 'shield',     Icon: Shield },
  { name: 'users',      Icon: Users },
];

function getPreloadedIcon(name) {
  return PRELOADED_ICONS.find(i => i.name === name)?.Icon || Globe;
}

// ── Small user badge ──────────────────────────────────────────────────────────
function UserBadge({ label, onRemove }) {
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-orange-100 text-orange-800 text-xs font-medium border border-orange-200">
      {label}
      {onRemove && (
        <button onClick={onRemove} className="hover:text-red-600 transition-colors ml-0.5">
          <X className="w-3 h-3" />
        </button>
      )}
    </span>
  );
}

// ── User selector dropdown ────────────────────────────────────────────────────
function UserSelector({ users, selectedIds, onChange, placeholder = 'Add user…' }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const filtered = users.filter(u =>
    !selectedIds.includes(u.id) &&
    (u.display_name || u.email || '').toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(v => !v)}
        className="flex items-center gap-1 px-2.5 py-1 rounded-lg border border-dashed border-orange-300 text-orange-600 text-xs hover:bg-orange-50 transition-colors"
      >
        <Plus className="w-3 h-3" />
        {placeholder}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 w-56 bg-white border border-gray-200 rounded-xl shadow-xl py-1">
          <div className="px-2 pb-1">
            <input
              autoFocus
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="Search users…"
              className="w-full px-2 py-1 text-xs border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
            />
          </div>
          <div className="max-h-40 overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="px-3 py-2 text-xs text-gray-400">No users found</div>
            ) : filtered.map(u => (
              <button
                key={u.id}
                onClick={() => { onChange([...selectedIds, u.id]); setQ(''); setOpen(false); }}
                className="w-full text-left px-3 py-1.5 text-xs hover:bg-orange-50 transition-colors text-gray-800"
              >
                {u.display_name || u.email}
                {u.role === 'super_admin' && <span className="ml-1 text-[10px] text-purple-500">(super admin)</span>}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Icon picker ───────────────────────────────────────────────────────────────
function IconPicker({ value, iconData, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const Cur = iconData ? null : getPreloadedIcon(value);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center hover:border-orange-400 hover:bg-orange-50 transition-all"
        title="Change icon"
      >
        {iconData
          ? <img src={iconData} alt="icon" className="w-4 h-4 object-contain" />
          : Cur ? <Cur className="w-4 h-4 text-gray-600" /> : <Globe className="w-4 h-4 text-gray-400" />}
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl p-2 w-48">
          <div className="grid grid-cols-6 gap-1 mb-2">
            {PRELOADED_ICONS.map(({ name, Icon }) => (
              <button
                key={name}
                onClick={() => { onChange({ iconType: 'preloaded', iconName: name, iconData: null }); setOpen(false); }}
                className={`w-7 h-7 rounded-lg flex items-center justify-center hover:bg-orange-50 transition-colors border ${value === name && !iconData ? 'border-orange-400 bg-orange-50' : 'border-transparent'}`}
                title={name}
              >
                <Icon className="w-4 h-4 text-gray-600" />
              </button>
            ))}
          </div>
          <label className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg border border-dashed border-gray-300 cursor-pointer hover:border-orange-400 hover:bg-orange-50 transition-colors text-xs text-gray-600">
            <Upload className="w-3 h-3" />
            Upload icon
            <input
              type="file"
              accept="image/*"
              className="hidden"
              onChange={e => {
                const file = e.target.files?.[0];
                if (!file) return;
                const reader = new FileReader();
                reader.onload = ev => {
                  onChange({ iconType: 'custom', iconName: null, iconData: ev.target.result });
                  setOpen(false);
                };
                reader.readAsDataURL(file);
                e.target.value = '';
              }}
            />
          </label>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ManagementToolsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState([]);
  const [tabs, setTabs] = useState({});         // { tab_id: [user_ids] }
  const [customUrls, setCustomUrls] = useState([]); // [{id, url, display_name, icon_type, icon_name, icon_data, allowed_users}]
  const [confirmGrant, setConfirmGrant] = useState(null); // { userId, context }
  const [newUrl, setNewUrl] = useState({ display_name: '', url: '', icon_type: 'preloaded', icon_name: 'globe', icon_data: null });
  const [addingUrl, setAddingUrl] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`${API}/management-tools/settings`);
      setUsers(r.data.users || []);
      const s = r.data.settings || {};
      setTabs(s.tabs || {});
      setCustomUrls(s.custom_urls || []);
    } catch { toast.error('Failed to load Management Tools settings'); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const save = async (newTabs, newUrls) => {
    setSaving(true);
    try {
      await api.put(`${API}/management-tools/settings`, {
        settings: { tabs: newTabs ?? tabs, custom_urls: newUrls ?? customUrls },
      });
      toast.success('Management Tools settings saved');
    } catch { toast.error('Failed to save settings'); }
    setSaving(false);
  };

  const getUserLabel = id => {
    const u = users.find(x => x.id === id);
    return u?.display_name || u?.email || id;
  };

  // Check if user has management tools pill access
  const userHasToolsAccess = id => {
    const u = users.find(x => x.id === id);
    return u?.can_access_tools === true || u?.role === 'super_admin';
  };

  // Add user to a tab, with smart prompt if they don't have pill access
  const addUserToTab = (tabId, userId) => {
    if (!userHasToolsAccess(userId)) {
      setConfirmGrant({ userId, context: `tab "${TABS.find(t => t.id === tabId)?.label}"`, onConfirm: async () => {
        await api.put(`${API}/users/${userId}/permissions`, { can_access_tools: true });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, can_access_tools: true } : u));
        toast.success(`Management Tools access granted to ${getUserLabel(userId)}`);
      }});
    }
    const newTabs = { ...tabs, [tabId]: [...(tabs[tabId] || []), userId] };
    setTabs(newTabs);
    save(newTabs, null);
  };

  const removeUserFromTab = (tabId, userId) => {
    const newTabs = { ...tabs, [tabId]: (tabs[tabId] || []).filter(id => id !== userId) };
    setTabs(newTabs);
    save(newTabs, null);
  };

  const addUserToUrl = (urlId, userId) => {
    if (!userHasToolsAccess(userId)) {
      setConfirmGrant({ userId, context: `URL "${customUrls.find(u => u.id === urlId)?.display_name}"`, onConfirm: async () => {
        await api.put(`${API}/users/${userId}/permissions`, { can_access_tools: true });
        setUsers(prev => prev.map(u => u.id === userId ? { ...u, can_access_tools: true } : u));
        toast.success(`Management Tools access granted to ${getUserLabel(userId)}`);
      }});
    }
    const newUrls = customUrls.map(u => u.id === urlId ? { ...u, allowed_users: [...(u.allowed_users || []), userId] } : u);
    setCustomUrls(newUrls);
    save(null, newUrls);
  };

  const removeUserFromUrl = (urlId, userId) => {
    const newUrls = customUrls.map(u => u.id === urlId ? { ...u, allowed_users: (u.allowed_users || []).filter(id => id !== userId) } : u);
    setCustomUrls(newUrls);
    save(null, newUrls);
  };

  const addCustomUrl = () => {
    if (!newUrl.display_name.trim() || !newUrl.url.trim()) { toast.error('Display name and URL are required'); return; }
    const entry = { id: crypto.randomUUID(), ...newUrl, allowed_users: [] };
    const newUrls = [...customUrls, entry];
    setCustomUrls(newUrls);
    setNewUrl({ display_name: '', url: '', icon_type: 'preloaded', icon_name: 'globe', icon_data: null });
    setAddingUrl(false);
    save(null, newUrls);
  };

  const deleteCustomUrl = (urlId) => {
    const newUrls = customUrls.filter(u => u.id !== urlId);
    setCustomUrls(newUrls);
    save(null, newUrls);
  };

  // Summary: every user who has access to any tab or URL
  const usersWithAccess = users.filter(u => {
    if (u.role === 'super_admin') return true;
    const inTab = Object.values(tabs).some(arr => (arr || []).includes(u.id));
    const inUrl = customUrls.some(url => (url.allowed_users || []).includes(u.id));
    return inTab || inUrl;
  });

  const getWhatUserCanSee = (uid) => {
    if (users.find(u => u.id === uid)?.role === 'super_admin') return ['All tabs', 'All URLs'];
    const seenTabs = TABS.filter(t => (tabs[t.id] || []).includes(uid)).map(t => t.label);
    const seenUrls = customUrls.filter(u => (u.allowed_users || []).includes(uid)).map(u => u.display_name);
    return [...seenTabs, ...seenUrls];
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 py-8 justify-center text-gray-400">
        <Loader2 className="w-5 h-5 animate-spin" />
        <span>Loading Management Tools settings…</span>
      </div>
    );
  }

  return (
    <div className="rounded-2xl border-2 border-orange-200 bg-orange-50/40 p-5 space-y-6 mb-6" data-testid="management-tools-settings">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: enconOrange }}>
          <Wrench className="w-5 h-5 text-white" />
        </div>
        <div>
          <h3 className="text-base font-bold text-gray-900">Management Tools</h3>
          <p className="text-xs text-gray-500">Control which users see which tabs and links in the Management Tools dropdown</p>
        </div>
        {saving && <Loader2 className="w-4 h-4 animate-spin text-orange-500 ml-auto" />}
      </div>

      {/* ── Summary: everyone with access ─────────────────────────── */}
      <div>
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5" />
          Users with Management Tools access
        </h4>
        {usersWithAccess.length === 0 ? (
          <p className="text-xs text-gray-400 italic">No users have been granted access yet</p>
        ) : (
          <div className="space-y-1.5">
            {usersWithAccess.map(u => {
              const canSee = getWhatUserCanSee(u.id);
              return (
                <div key={u.id} className="flex items-start gap-2 bg-white rounded-xl px-3 py-2 border border-orange-100 text-xs">
                  <div className="font-semibold text-gray-800 min-w-[120px]">{u.display_name || u.email}</div>
                  <div className="flex flex-wrap gap-1">
                    {canSee.map(s => (
                      <span key={s} className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[11px] border border-orange-200">{s}</span>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Per-tab permissions ────────────────────────────────────── */}
      <div>
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2">Dropdown Tab Permissions</h4>
        <div className="space-y-2">
          {TABS.map(({ id, label, Icon }) => {
            const assignedIds = tabs[id] || [];
            const unassigned = users.filter(u => !assignedIds.includes(u.id));
            return (
              <div key={id} className="bg-white rounded-xl border border-gray-200 px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Icon className="w-4 h-4 text-gray-500" />
                  <span className="text-sm font-semibold text-gray-800">{label}</span>
                  <span className="text-[11px] text-gray-400 ml-auto">{assignedIds.length} user{assignedIds.length !== 1 ? 's' : ''}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {assignedIds.map(uid => (
                    <UserBadge key={uid} label={getUserLabel(uid)} onRemove={() => removeUserFromTab(id, uid)} />
                  ))}
                  <UserSelector users={unassigned} selectedIds={assignedIds} onChange={ids => {
                    const newId = ids[ids.length - 1];
                    if (newId) addUserToTab(id, newId);
                  }} />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Custom URLs ────────────────────────────────────────────── */}
      <div>
        <h4 className="text-xs font-bold text-gray-700 uppercase tracking-wide mb-2 flex items-center gap-1.5">
          <Globe className="w-3.5 h-3.5" />
          Custom URL Links
        </h4>
        <div className="space-y-2">
          {customUrls.map(url => {
            const UrlIcon = url.icon_type === 'custom' ? null : getPreloadedIcon(url.icon_name);
            const unassigned = users.filter(u => !(url.allowed_users || []).includes(u.id));
            return (
              <div key={url.id} className="bg-white rounded-xl border border-gray-200 px-4 py-3 space-y-2" data-testid={`mt-url-${url.id}`}>
                <div className="flex items-center gap-2">
                  <div className="w-7 h-7 rounded-lg border border-gray-200 flex items-center justify-center flex-shrink-0">
                    {url.icon_type === 'custom' && url.icon_data
                      ? <img src={url.icon_data} alt="" className="w-4 h-4 object-contain" />
                      : UrlIcon ? <UrlIcon className="w-4 h-4 text-gray-500" /> : <Globe className="w-4 h-4 text-gray-400" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-gray-800">{url.display_name}</div>
                    <a href={url.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-blue-500 hover:underline flex items-center gap-1 truncate max-w-xs">
                      {url.url} <ExternalLink className="w-2.5 h-2.5 flex-shrink-0" />
                    </a>
                  </div>
                  <button onClick={() => deleteCustomUrl(url.id)} className="text-gray-400 hover:text-red-500 transition-colors" title="Delete link">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 items-center">
                  {(url.allowed_users || []).map(uid => (
                    <UserBadge key={uid} label={getUserLabel(uid)} onRemove={() => removeUserFromUrl(url.id, uid)} />
                  ))}
                  <UserSelector users={unassigned} selectedIds={url.allowed_users || []} onChange={ids => {
                    const newId = ids[ids.length - 1];
                    if (newId) addUserToUrl(url.id, newId);
                  }} placeholder="Add user…" />
                </div>
              </div>
            );
          })}
        </div>

        {/* Add new URL form */}
        {addingUrl ? (
          <div className="bg-white rounded-xl border-2 border-dashed border-orange-300 px-4 py-3 mt-2 space-y-3">
            <p className="text-xs font-semibold text-gray-700">New Link</p>
            <div className="flex gap-2 items-center">
              <IconPicker
                value={newUrl.icon_name}
                iconData={newUrl.icon_data}
                onChange={({ iconType, iconName, iconData }) =>
                  setNewUrl(v => ({ ...v, icon_type: iconType, icon_name: iconName, icon_data: iconData }))
                }
              />
              <input
                value={newUrl.display_name}
                onChange={e => setNewUrl(v => ({ ...v, display_name: e.target.value }))}
                placeholder="Display name"
                className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
                data-testid="mt-url-name-input"
              />
            </div>
            <input
              value={newUrl.url}
              onChange={e => setNewUrl(v => ({ ...v, url: e.target.value }))}
              placeholder="https://..."
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-orange-400"
              data-testid="mt-url-input"
            />
            <div className="flex gap-2">
              <button onClick={addCustomUrl} className="px-3 py-1.5 rounded-lg text-white text-xs font-semibold flex items-center gap-1" style={{ background: enconOrange }} data-testid="mt-url-save-btn">
                <Check className="w-3.5 h-3.5" /> Add Link
              </button>
              <button onClick={() => { setAddingUrl(false); setNewUrl({ display_name: '', url: '', icon_type: 'preloaded', icon_name: 'globe', icon_data: null }); }} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs font-semibold" data-testid="mt-url-cancel-btn">
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setAddingUrl(true)}
            className="mt-2 flex items-center gap-2 px-3 py-2 rounded-xl border border-dashed border-orange-300 text-orange-600 text-xs font-semibold hover:bg-orange-50 transition-colors w-full justify-center"
            data-testid="mt-add-url-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Add Custom Link
          </button>
        )}
      </div>

      {/* ── Smart prompt: grant management tools access ─────────────── */}
      {confirmGrant && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl border border-orange-200 p-6 max-w-sm w-full space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center flex-shrink-0">
                <AlertCircle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="font-bold text-gray-900 text-sm">Grant Management Tools access?</h3>
                <p className="text-xs text-gray-600 mt-1">
                  <strong>{getUserLabel(confirmGrant.userId)}</strong> doesn't currently have access to the Management Tools pill.
                  You're adding them to <strong>{confirmGrant.context}</strong>.
                  Would you like to also grant them Management Tools pill access?
                </p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={async () => { await confirmGrant.onConfirm(); setConfirmGrant(null); }}
                className="flex-1 px-3 py-2 rounded-xl text-white text-sm font-semibold"
                style={{ background: enconOrange }}
                data-testid="mt-grant-confirm-yes"
              >
                Yes, grant access
              </button>
              <button
                onClick={() => setConfirmGrant(null)}
                className="flex-1 px-3 py-2 rounded-xl bg-gray-100 text-gray-700 text-sm font-semibold"
                data-testid="mt-grant-confirm-no"
              >
                No thanks
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
